import {FC, useMemo} from 'react'
import {ScheduleCompletion} from './ScheduleCompletion'
import {useStore} from '../../store'
import {
  filterEventsBetween,
  getAllowableValues,
  getBusinessDay,
  getHoursOfOperationOnDay,
  isSlotCovered,
  isUnassignedEventRowId,
} from '../../helpers/generals'
import {addDays, addMinutes, eachMinuteOfInterval, format, startOfWeek} from 'date-fns'
import {useLocation, useNavigate} from 'react-router-dom'
import {string} from 'yup'

const ScheduleCompletionContainer: FC = () => {
  const {
    week,
    events,
    handleState,
    handleMultipleStates,
    resources,
    selectedDate,
    selectedPosition,
    availablePositions,
    hoursOfOperations,
    allowableHoursDaily,
    headcountBoundsDaily,
    shiftsLoaded,
    allowableHoursLoaded,
    headcountBoundsLoaded,
    view,
  } = useStore()

  // The week arrives in four separate responses (hours of operation, shifts,
  // allowable figures, measure-derived bounds) and every one of them moves the
  // percentage. Scoring whatever has landed so far doesn't show an early
  // answer, it shows a wrong one — allowable figures without their bounds
  // fallback give measure-driven slots a 0–0 band, so empty slots read as done
  // and the gauges start high; bands without shifts read the opposite way and
  // they start at zero. Hold at "—" until the whole week has settled.
  const dataReady =
    hoursOfOperations !== null && shiftsLoaded && allowableHoursLoaded && headcountBoundsLoaded

  const navigate = useNavigate()
  const location = useLocation()
  const {weekStartOn, weekDays, step} = week!
  const _weekStart = startOfWeek(selectedDate, {weekStartsOn: weekStartOn})
  const _weekStartKey = _weekStart.getTime()
  const daysList = weekDays.map((d) => addDays(_weekStart, d))

  const getScheduleCompletionofDay = (dayDate: Date): [number, number, boolean] => {
    const [dayStart, dayEnd] = getHoursOfOperationOnDay(dayDate, hoursOfOperations)
    const dayEvents = events.filter(
      (x) => getBusinessDay(x.start, hoursOfOperations).getDate() === dayDate.getDate()
    )
    const positionEvents =
      selectedPosition === 'all'
        ? dayEvents
        : dayEvents.filter((e) =>
            resources
              .filter((emp) => emp.position === selectedPosition)
              .some((emp) => emp.employee_id === e.employee_id)
          )

    const START_TIME = dayStart
    const END_TIME = addMinutes(dayEnd, -30)
    // Guard: an open span shorter than the 30-min step (bad hours-of-operation
    // data) would make eachMinuteOfInterval throw RangeError: Invalid interval
    // and white-screen the whole scheduler.
    if (END_TIME < START_TIME) {
      return [0, 0, false]
    }
    const hours = eachMinuteOfInterval(
      {
        start: START_TIME,
        end: END_TIME,
      },
      {step: 30}
    )

    const allowableValues = getAllowableValues(
      allowableHoursDaily,
      dayDate,
      selectedPosition,
      hours,
      headcountBoundsDaily
    )

    const headcount_used = hours.map((h, i) => {
      const headCountsUsed = filterEventsBetween(
        positionEvents,
        h,
        addMinutes(h, step),
        undefined,
        true,
        availablePositions
      ).length

      if (allowableValues.length === 0) {
        return {headCountsUsed, tempStriclyWrongSchedule: false}
      }

      const maxHeadCounts = allowableValues[i].max_headcount
      const minHeadCounts = allowableValues[i].min_headcount
      var tempStriclyWrongSchedule = false
      if (
        (headCountsUsed > maxHeadCounts || headCountsUsed < minHeadCounts) &&
        allowableValues[i].is_strict
      ) {
        tempStriclyWrongSchedule = true
      }
      return {headCountsUsed, tempStriclyWrongSchedule}
    })

    if (allowableValues.length !== 0) {
      const done = headcount_used.reduce((acc, currentValue, index) => {
        return isSlotCovered(currentValue.headCountsUsed, allowableValues[index]) ? acc + 1 : acc
      }, 0)
      return [headcount_used.length, done, headcount_used.some((x) => x.tempStriclyWrongSchedule)]
    } else {
      return [0, 0, false]
    }
  }

  const getScheduleCompletion = (): {completionRates: any[]; strictlyUnacceptables: boolean[]} => {
    var completionRates: any[] = []
    var strictlyUnacceptables: boolean[] = []
    if (dataReady) {
      var totalSlots: number = 0
      var totalDone: number = 0
      for (var i = 0; i < daysList.length; i++) {
        const [daySlots, dayDone, dayStrictlyUnacceptable] = getScheduleCompletionofDay(daysList[i])
        // Any unassigned shift on this business day flags the day red — a
        // shift nobody is on still needs staffing whatever put it there:
        // an applied template, an accepted recommendation, cleared
        // assignments, or an auto-fill that couldn't staff every shift.
        // (This used to be gated on the schedule's template_applied flag,
        // back when a template was the only way to get one; every other
        // source leaves that flag false — a proposal applied with `replace`
        // even clears it — so the warning silently stopped appearing.)
        // Unassigned events carry a sentinel row id, NOT employee_id null:
        // convertLaborShiftToEvent maps null to `unassigned-<shift id>` so
        // each shift owns a grid row — so detect via that sentinel (an
        // `=== null` check here silently never matches). Business-day
        // grouping matches getScheduleCompletionofDay (overnight shifts
        // count on the prior day). All positions, regardless of the
        // position filter, so the warning isn't hidden when one is selected.
        const dayHasEmptyShift = events.some(
          (e) =>
            (e.employee_id === null || isUnassignedEventRowId(e.employee_id)) &&
            getBusinessDay(e.start, hoursOfOperations).getDate() === daysList[i].getDate()
        )
        completionRates.push({
          dayDate: daysList[i],
          daySlots: daySlots,
          dayDone: dayDone,
          completion: Math.round((dayDone / daySlots) * 100) || 0,
          hasEmptyShift: dayHasEmptyShift,
        })
        strictlyUnacceptables.push(dayStrictlyUnacceptable)
        totalSlots += daySlots
        totalDone += dayDone
      }

      return {completionRates, strictlyUnacceptables}
    } else {
      for (var i = 0; i < daysList.length; i++) {
        completionRates.push({
          dayDate: daysList[i],
          daySlots: 0,
          dayDone: 0,
          completion: 0,
        })
        strictlyUnacceptables.push(false)
      }
    }
    return {completionRates, strictlyUnacceptables}
  }

  const completions: {completionRates: any[]; strictlyUnacceptables: boolean[]} = useMemo(() => {
    // console.log('events.length', events.length)
    return getScheduleCompletion() || {completionRates: [], strictlyUnacceptables: []}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    events,
    hoursOfOperations,
    allowableHoursDaily,
    headcountBoundsDaily,
    selectedPosition,
    resources,
    availablePositions,
    // Completions cover the whole displayed week — keying on the week start
    // (not selectedDate) lets same-week gauge clicks reuse the memo.
    _weekStartKey,
    week,
    dataReady,
  ])

  const weekCompletion =
    Math.round(
      (completions.completionRates.reduce((acc, currentValue) => {
        return acc + currentValue.dayDone
      }, 0) /
        completions.completionRates.reduce((acc, currentValue) => {
          return acc + currentValue.daySlots
        }, 0)) *
        100
    ) || 0

  const handleGaugeClick = (dayDate: Date) => {
    // handleState(dayDate, 'selectedDate')
    // handleState('timeline', 'view')
    const states = new Map<string, any>([
      ['selectedDate', dayDate],
      ['view', 'timeline'],
    ])
    handleMultipleStates(states)
    if (window.location.pathname !== '/labor-scheduling/scheduling/schedule') {
      navigate('/labor-scheduling/scheduling/schedule')
    }
  }

  const handleWeekClick = () => {
    if (selectedDate !== _weekStart || view !== 'week') {
      // handleState(_weekStart, 'selectedDate')
      // handleState('employeesWeek', 'view')

      const states = new Map<string, any>([
        ['selectedDate', _weekStart],
        ['view', 'employeesWeek'],
      ])
      handleMultipleStates(states)
    }
    if (window.location.pathname !== '/labor-scheduling/scheduling/schedule') {
      navigate('/labor-scheduling/scheduling/schedule')
    }
  }
  const wrapper = useMemo(() => {
    // console.log('RENDERING CONTAINERRRRRRRRRRRRR')
    // console.log('hoursOfOperations', hoursOfOperations)
    // console.log('daysList', daysList)

    return (
      <div className='d-flex flex-wrap align-items-center'>
        {completions.completionRates.map((c, i) => {
          return (
            <a
              key={format(c.dayDate, 'EE')}
              onClick={(e) => handleGaugeClick(c.dayDate)}
              style={{cursor: 'pointer'}}
            >
              <ScheduleCompletion
                chartKey={format(c.dayDate, 'EE')}
                day={format(c.dayDate, 'EE')}
                completion={c.completion}
                pending={!dataReady}
                strictlyUnacceptable={completions.strictlyUnacceptables[i]}
                hasEmptyShift={c.hasEmptyShift}
                currentDay={
                  view === 'timeline' && format(c.dayDate, 'EE') === format(selectedDate, 'EE')
                }
              />
            </a>
          )
        })}

        <div className='vr mx-4 my-3'></div>
        <a key='week' onClick={(e) => handleWeekClick()} style={{cursor: 'pointer'}}>
          <ScheduleCompletion
            chartKey='week'
            day='Week'
            strictlyUnacceptable={false}
            completion={weekCompletion}
            pending={!dataReady}
            currentDay={view === 'employeesWeek'}
          />
        </a>
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedDate, completions, dataReady])
  return wrapper
}

export {ScheduleCompletionContainer}
